import React, { useState, useEffect, useMemo } from 'react';
import { X, LayoutDashboard, Filter, CreditCard, User, MapPin, Loader2, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

/**
 * Mapeamento estático e preciso das posições percentuais das regiões
 * baseado diretamente no contorno físico do mapa de Minas Gerais.
 */
const obterPosicaoRegiao = (nomeRegiao, ufSigla) => {
    const regiao = String(nomeRegiao || '').trim().toLowerCase();
    const siglaEstado = String(ufSigla || '').trim().toLowerCase();
   
    // Se o backend enviar um estado explicitamente e este NÃO for Minas Gerais, joga para o canto superior (ex: Curitiba/PR)
    if (siglaEstado !== '' && siglaEstado !== 'mg' && siglaEstado !== 'bra') {
        return { top: '0%', left: '0%' };
    }
    console.log(nomeRegiao)
     console.log(regiao)
    if (regiao.includes('Noroeste de Minas') || regiao.includes('noroeste de minas') ) {          
         return { top: '40%', left: '44%' }; // Noroeste (Esquerda alta)
    }
     if (regiao.includes('norte de minas')) {
       return { top: '20%', left: '68%' }; // Topo do mapa
    }
    if (regiao.includes('jequitinhonha')) {       
        return { top: '30%', left: '85%' }; // Nordeste (Direita alta)
    }
    if (regiao.includes('Vale do Mucuri') || regiao.includes('mucuri')) {     
        return { top: '40%', left: '88%' };
    } 
    // Sul e Vertentes
    if (regiao.includes('sul / sudoeste') || regiao.includes('sul de minas')) {
        return { top: '85%', left: '50%' };
    }
    if (regiao.includes('campo das vertentes')) {
        return { top: '78%', left: '60%' };
    }
      if (regiao.includes('vale do rio doce') || regiao.includes('vale do doce')) {
        return { top: '55%', left: '80%' };
    }
    if (regiao.includes('zona da mata')) {
        return { top: '75%', left: '76%' };
    }
    if (regiao.includes('oeste de minas')) {
        return { top: '68%', left: '56%' };
    }
    // Região Central / Metropolitana (Grande BH, Metropolitana, etc.)
    if (regiao.includes('grande bh') || regiao.includes('belo horizonte')) {
         return { top: '65%', left: '70%' };
    }
    if (regiao.includes('Regiao Metropolitana de BH')) {
         return { top: '65%', left: '50%' };
    }
    if (regiao.includes('central mineira')) {
        return { top: '55%', left: '55%' };
    }
    if (regiao.includes('triangulo') || regiao.includes('triângulo')) {
        return { top: '58%', left: '38%' };
    }

    if (!siglaEstado.includes('MG')) {
        return { top: '0%', left: '0%' };
    }

    // Fallback padrão seguro perto do centro caso apareça alguma variação de string inesperada
    return { top: '50%', left: '52%' };
};

export default function GraficosDashboardCompleto({ onClose }) {
    const base_url = 'https://onboard.fck.ind.br';

    // Estados de controle e dados
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtros operacionais
    const [anoInicio, setAnoInicio] = useState(2023);
    const [anoFinal, setAnoFinal] = useState(2026);
    const [empresaFiltro, setEmpresaFiltro] = useState('TODAS');

    // Filtro de ano selecionado em tela
    const [anoAtivoVisual, setAnoAtivoVisual] = useState('2026');

    // Cores do Tema Computador de Bordo e Layout
    const CORES_PIE = ['#ff7b72', '#58a6ff', '#f0883e', '#8b949e', '#7ee787', '#d4bbff'];
    const COR_BARRA_VENDEDOR = '#f0883e';
    const COR_BARRA_REGIAO = '#ff7b72';

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('@user_token');

            if (!token) {
                setError("Sessão expirada. Por favor, faça login novamente.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const params = new URLSearchParams({
                    anoInicio,
                    anoFinal,
                    empresa: empresaFiltro
                });

                const res = await fetch(`${base_url}/api/dashboard/Faturamento/graficogeral?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!res.ok) {
                    if (res.status === 401) throw new Error('Não autorizado. Token inválido.');
                    throw new Error('Erro ao buscar dados do servidor');
                }

                const result = await res.json();
                setData(result);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [anoInicio, anoFinal, empresaFiltro]);

    // Filtra os dados na memória de acordo com o ano ativo selecionado no botão lateral
    const dadosFiltradosAnoVisual = useMemo(() => {
        return data.filter(item => String(item?.ano) === String(anoAtivoVisual));
    }, [data, anoAtivoVisual]);

    // 1. Gráfico Lateral Esquerdo: Agrupado por CIDADE
    const dadosCidades = useMemo(() => {
        const agrupado = dadosFiltradosAnoVisual.reduce((acc, curr) => {
            const cidadeRaw = curr?.cidade || curr?.CIDADE || `Cidade ${curr?.mês || 'M'}`;
            const cidade = cidadeRaw && cidadeRaw.trim() !== "" ? cidadeRaw.trim() : "Não Informada";
            const valor = Number(curr?.valor_Total || 0);
            
            if (!acc[cidade]) {
                acc[cidade] = { name: cidade, valor: 0 };
            }
            acc[cidade].valor += valor;
            return acc;
        }, {});

        return Object.values(agrupado).sort((a, b) => b.valor - a.valor);
    }, [dadosFiltradosAnoVisual]);

    // 1.2 Mapa (Painel Direito): Agrupado estritamente por REGIÃO para plotagem limpa
    const dadosRegiao = useMemo(() => {
        const agrupado = dadosFiltradosAnoVisual.reduce((acc, curr) => {
            const regiaoRaw = curr?.regiao || curr?.REGIAO || curr?.cidade || `Região ${curr?.mês || 'M'}`;
            const regiao = regiaoRaw && regiaoRaw.trim() !== "" ? regiaoRaw.trim() : "Não Informada";
            const valor = Number(curr?.valor_Total || 0);
            const ufSigla = curr?.estado || curr?.ESTADO;
             
            if (!acc[regiao]) {
                acc[regiao] = { name: regiao, valor: 0, sigla: ufSigla };
            }

            acc[regiao].valor += valor;
            return acc;
        }, {});

        return Object.values(agrupado).sort((a, b) => b.valor - a.valor);
    }, [dadosFiltradosAnoVisual]);

    // 2. Inferior Esquerdo: VENDAS por VENDEDOR
    const dadosVendedor = useMemo(() => {
        const agrupado = dadosFiltradosAnoVisual.reduce((acc, curr) => {
            const vendedorRaw = curr?.vendedor || curr?.VENDEDOR;
            const vendedor = vendedorRaw && vendedorRaw.trim() !== "" ? vendedorRaw.trim() : "Não Identificado";
            const valor = Number(curr?.valor_Total || curr?.VALORTOTAL || 0);
            acc[vendedor] = (acc[vendedor] || 0) + valor;
            return acc;
        }, {});

        return Object.entries(agrupado)
            .map(([name, valor]) => ({ name, valor }))
            .sort((a, b) => b.valor - a.valor);
    }, [dadosFiltradosAnoVisual]);

    // 3. Inferior Central: COMPARAÇÃO ENTRE PAGAMENTOS
    const dadosPagamento = useMemo(() => {
        const agrupado = dadosFiltradosAnoVisual.reduce((acc, curr) => {
            const formaRaw = curr?.forma_pagamento || curr?.FORMA_PAGAMENTO;
            const forma = formaRaw && formaRaw.trim() !== "" ? formaRaw.trim() : "Não Informado";
            const valor = Number(curr?.valor_Total || curr?.VALORTOTAL || 0);

            acc[forma] = (acc[forma] || 0) + valor;
            return acc;
        }, {});

        return Object.entries(agrupado)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [dadosFiltradosAnoVisual]);

    // 4. Centro: Cards Dinâmicos de Indicadores (KPIs)
    const cardsIndicadores = useMemo(() => {
        if (dadosRegiao.length === 0) {
            return { melhorForma: 'Pix', vlrForma: 0, topRegiao: '-', vlrRegiao: 0, topVendedor: '-', vlrVendedor: 0 };
        }
        return {
            melhorForma: dadosPagamento[0]?.name || 'Não Informado',
            vlrForma: dadosPagamento[0]?.value || 0,
            topRegiao: dadosRegiao[0]?.name || 'Região Central',
            vlrRegiao: dadosRegiao[0]?.valor || 0,
            topVendedor: dadosVendedor[0]?.name || 'Não Informado',
            vlrVendedor: dadosVendedor[0]?.valor || 0
        };
    }, [dadosRegiao, dadosVendedor, dadosPagamento]);

    const formatK = (val) => `R$ ${(Number(val) / 1000).toFixed(0)}K`;
    const formatBRL = (val) => Number(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    if (loading) {
        return (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-md">
                <div className="text-blue-500 font-mono italic animate-pulse flex flex-col items-center">
                    <Loader2 size={48} className="mb-4 animate-spin" />
                    <span className="tracking-[0.3em] text-xs">BUSCANDO DADOS ESTATÍSTICOS...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-md">
                <div className="text-red-500 flex flex-col items-center max-w-md text-center p-6 bg-[#161b22] border border-gray-800 rounded-lg">
                    <AlertCircle size={40} className="mb-2" />
                    <h2 className="font-bold uppercase text-sm tracking-wider mb-2">Falha na Requisição</h2>
                    <p className="text-xs text-gray-400 mb-4">{error}</p>
                    <button onClick={onClose} className="bg-red-600 hover:bg-red-500 text-white text-[10px] font-black py-2 px-6 rounded uppercase">Fechar</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-md p-2 lg:p-4">
            <div className="bg-[#0d1117] border border-gray-800 w-full max-w-[1450px] h-[95vh] rounded-lg shadow-2xl overflow-hidden flex flex-col font-sans">

                {/* Cabeçalho */}
                <div className="flex justify-between items-center p-4 border-b border-gray-800 bg-[#161b22]">
                    <div className="flex items-center gap-2 text-blue-400 font-bold tracking-widest text-xs uppercase">
                        <LayoutDashboard size={18} />
                        <span>Dashboard Estatístico Analítico</span>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Controles de Filtros da API */}
                <div className="p-4 bg-[#0d1117] border-b border-gray-800 flex flex-wrap gap-6 items-end">
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase font-bold">Ano Início (API)</label>
                        <select value={anoInicio} onChange={e => setAnoInicio(Number(e.target.value))} className="bg-[#161b22] border border-gray-700 text-gray-300 text-xs rounded p-2 outline-none focus:border-blue-500 font-mono">
                            {[2022, 2023, 2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase font-bold">Ano Final (API)</label>
                        <select value={anoFinal} onChange={e => setAnoFinal(Number(e.target.value))} className="bg-[#161b22] border border-gray-700 text-gray-300 text-xs rounded p-2 outline-none focus:border-blue-500 font-mono">
                            {[2022, 2023, 2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase font-bold">Empresa</label>
                        <select value={empresaFiltro} onChange={e => setEmpresaFiltro(e.target.value)} className="bg-[#161b22] border border-gray-700 text-gray-300 text-xs rounded p-2 outline-none focus:border-blue-500">
                            <option value="TODAS">TODAS</option>
                            <option value="1">FCK SJL</option>
                            <option value="2">FCK CTG</option>
                            <option value="3">FCK BHZ</option>
                        </select>
                    </div>
                    <div className="ml-auto text-gray-500 flex items-center gap-2">
                        <Filter size={14} />
                        <span className="text-[10px] uppercase font-mono text-green-500">Parâmetros Sincronizados com Sucesso</span>
                    </div>
                </div>

                {/* Dashboard de Painéis */}
                <div className="flex-1 overflow-y-auto p-4 lg:p-6 flex flex-row gap-4">

                    {/* Seletor Lateral */}
                    <div className="flex flex-col gap-2 justify-center pr-2 border-r border-gray-800">
                        {['2023', '2024', '2025', '2026'].map(ano => (
                            <button
                                key={ano}
                                onClick={() => setAnoAtivoVisual(ano)}
                                className={`py-3 px-4 rounded text-xs font-bold font-mono border transition-all ${anoAtivoVisual === ano
                                    ? 'bg-blue-600 border-blue-500 text-white shadow-lg scale-105'
                                    : 'bg-[#161b22] border-gray-800 text-gray-500 hover:text-gray-300'
                                    }`}
                            >
                                {ano}
                            </button>
                        ))}
                    </div>

                    {/* Área Principal dos Gráficos */}
                    <div className="flex-1 space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                            {/* Painel Esquerdo: Vendas por Cidade */}
                            <div className="lg:col-span-4 bg-[#161b22] border border-gray-800 rounded p-4 flex flex-col min-h-[320px]">
                                <div className="text-gray-400 font-bold text-[10px] uppercase tracking-wider mb-4">
                                    Comparação entre VENDAS por CIDADE
                                </div>
                                <div className="flex-1 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={dadosCidades.slice(0,8)} layout="vertical" margin={{ left: 5, right: 20 }}>
                                            <XAxis type="number" hide />
                                            <YAxis dataKey="name" type="category" stroke="#8b949e" fontSize={10} tickLine={false} axisLine={false} width={80} />
                                            <Tooltip formatter={(val) => [formatBRL(val), 'Faturamento']} contentStyle={{ backgroundColor: '#0d1117', border: '1px solid #30363d' }} />
                                            <Bar dataKey="valor" fill={COR_BARRA_REGIAO} radius={[0, 4, 4, 0]} barSize={14} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Painel Central: Três Cards Estatísticos */}
                            <div className="lg:col-span-4 flex flex-col justify-between gap-4">
                                <div className="bg-gradient-to-r from-[#161b22] to-[#1a202c] border border-gray-800 p-4 rounded shadow-md flex justify-between items-center">
                                    <div>
                                        <p className="text-gray-500 text-[9px] uppercase font-bold tracking-wider">Forma de pagamento mais utilizada</p>
                                        <p className="text-white text-base font-black mt-1 truncate max-w-[220px]">{cardsIndicadores.melhorForma}</p>
                                        <p className="text-gray-400 font-mono text-sm mt-0.5">{formatBRL(cardsIndicadores.vlrForma)}</p>
                                    </div>
                                    <CreditCard className="text-gray-700" size={28} />
                                </div>

                                <div className="bg-gradient-to-r from-[#161b22] to-[#1a202c] border border-gray-800 p-4 rounded shadow-md flex justify-between items-center">
                                    <div>
                                        <p className="text-gray-500 text-[9px] uppercase font-bold tracking-wider">Região com mais vendas</p>
                                        <p className="text-white text-base font-black mt-1 truncate max-w-[220px]">{cardsIndicadores.topRegiao}</p>
                                        <p className="text-green-400 font-mono text-sm font-bold mt-0.5">{formatBRL(cardsIndicadores.vlrRegiao)}</p>
                                    </div>
                                    <MapPin className="text-gray-700" size={28} />
                                </div>

                                <div className="bg-gradient-to-r from-[#161b22] to-[#1a202c] border border-gray-800 p-4 rounded shadow-md flex justify-between items-center">
                                    <div>
                                        <p className="text-gray-500 text-[9px] uppercase font-bold tracking-wider">Vendedor com mais vendas</p>
                                        <p className="text-white text-base font-black mt-1 truncate max-w-[220px]">{cardsIndicadores.topVendedor}</p>
                                        <p className="text-green-400 font-mono text-sm font-bold mt-0.5">{formatBRL(cardsIndicadores.vlrVendedor)}</p>
                                    </div>
                                    <User className="text-gray-700" size={28} />
                                </div>
                            </div>

                            {/* Painel Direito: Total Vendido x Estado (Mapa Macrorregiões Estático) */}
                            <div className="lg:col-span-4 bg-[#161b22] border border-gray-800 rounded p-4 flex flex-col min-h-[320px] relative">
                                <div className="text-gray-400 font-bold text-[10px] uppercase tracking-wider mb-2">
                                    Total Vendido x Estado (Posicionamento por Regiões)
                                </div>

                                <div className="flex-1 flex items-center justify-center relative bg-[#0d1117] rounded border border-gray-800/40 overflow-hidden w-full h-full">

                                    {/* Fundo do mapa real de Minas Gerais */}
                                    <img
                                        src="https://static.mundoeducacao.uol.com.br/mundoeducacao/2020/11/messoregioes-mg.png"
                                        alt="Mapa de Minas Gerais"
                                        className="w-full h-full max-h-[240px] object-contain opacity-750 select-none pointer-events-none invert hue-rotate-180 brightness-90 filter"
                                    />

                                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                                        {/* Mapeia dinamicamente cada ponto retornado da regra de strings */}
                                        {dadosRegiao.map((reg) => {
                                            // CORREÇÃO: Enviando reg.sigla (definida no reduce) em vez de reg.ufSigla (que vinha undefined)
                                            const posicao = obterPosicaoRegiao(reg.name, reg.sigla);
                                           
                                            return (
                                                <div
                                                    key={reg.name}
                                                    className="absolute flex items-center gap-1 transition-all duration-500 -translate-x-1/2 -translate-y-1/2"
                                                    style={{ top: posicao.top, left: posicao.left }}
                                                >
                                                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_6px_#ef4444] animate-ping absolute" />
                                                    <span className="h-1.5 w-1.5 rounded-full bg-red-600 z-10" />
                                                    <div className="bg-[#161b22]/95 border border-gray-800/60 rounded px-1 py-0.5 text-[7px] font-mono text-white whitespace-nowrap shadow-2xl backdrop-blur-sm pointer-events-auto">
                                                     <span className="text-green-400 font-bold">{formatK(reg.valor)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Segunda Linha de Gráficos */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                            {/* Esquerda: VENDAS POR VENDEDOR */}
                            <div className="bg-[#161b22] border border-gray-800 rounded p-4 flex flex-col min-h-[300px]">
                                <div className="text-gray-400 font-bold text-[10px] uppercase tracking-wider mb-4">
                                    VENDAS por VENDEDOR
                                </div>
                                <div className="flex-1 w-full">
                                    {dadosVendedor.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-gray-500 font-mono text-xs">
                                            Nenhum dado de vendedor encontrado
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={dadosVendedor.slice(0, 8)} margin={{ bottom: 10 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#30363d" vertical={false} />
                                                <XAxis dataKey="name" stroke="#8b949e" fontSize={10} tickLine={false} axisLine={false} />
                                                <YAxis stroke="#8b949e" fontSize={10} tickLine={false} axisLine={false} tickFormatter={formatK} />
                                                <Tooltip formatter={(val) => [formatBRL(val), 'Total']} contentStyle={{ backgroundColor: '#0d1117', border: '1px solid #30363d' }} />
                                                <Bar dataKey="valor" fill={COR_BARRA_VENDEDOR} radius={[4, 4, 0, 0]} barSize={32} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>

                            {/* Direita: COMPARAÇÃO ENTRE PAGAMENTOS */}
                            <div className="bg-[#161b22] border border-gray-800 rounded p-4 flex flex-col min-h-[300px]">
                                <div className="text-gray-400 font-bold text-[10px] uppercase tracking-wider mb-2">
                                    Comparação entre PAGAMENTOS
                                </div>
                                <div className="flex-1 w-full flex items-center justify-center">
                                    {dadosPagamento.length === 0 ? (
                                        <div className="text-gray-500 font-mono text-xs">Nenhum dado de pagamento encontrado</div>
                                    ) : (
                                        <>
                                            <div className="w-full h-full max-h-[220px]">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={dadosPagamento}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={60}
                                                            outerRadius={85}
                                                            paddingAngle={4}
                                                            dataKey="value"
                                                        >
                                                            {dadosPagamento.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={CORES_PIE[index % CORES_PIE.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => formatBRL(value)} contentStyle={{ backgroundColor: '#0d1117', border: '1px solid #30363d' }} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                            <div className="flex flex-col gap-2 pr-4 justify-center max-h-[220px] overflow-y-auto">
                                                {dadosPagamento.map((item, index) => (
                                                    <div key={item.name} className="flex items-center gap-2 text-[9px] uppercase font-bold tracking-wider">
                                                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CORES_PIE[index % CORES_PIE.length] }} />
                                                        <span className="text-gray-400 truncate max-w-[120px]" title={item.name}>{item.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>

                </div>

                {/* Rodapé */}
                <div className="p-4 bg-[#161b22] border-t border-gray-800 flex justify-between items-center text-[9px] uppercase font-mono text-gray-500">
                    <span>Processamento por Macrorregião Homologado com Sucesso</span>
                    <button onClick={onClose} className="bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black py-2 px-6 rounded transition-all uppercase tracking-wider">
                        Voltar ao Dashboard Geral
                    </button>
                </div>

            </div>
        </div>
    );
}